/**
 * Whole-building rotation about the vertical axis.
 *
 * The user rotates the sauna to sit it on the plot the way it will really
 * stand — entrance towards the path, glass towards the lake, long facade
 * towards the afternoon sun. The site itself (ground, lake, sun, dressing)
 * stays put; only the building turns.
 *
 * **The committed value is always a cardinal angle** (0 / 90 / 180 / 270).
 * A live gesture may pass through any angle, but the value that reaches the
 * store — and therefore geometry, persistence and undo — is snapped. That is
 * a deliberate constraint, not a shortcut: every world-space consumer in the
 * viewer (pick planes, grounding footprint, section axis, grass keep-out)
 * assumes axis-aligned walls, and at a cardinal angle those assumptions stay
 * exactly true with a swap of the two horizontal axes. An arbitrary angle
 * would silently break all of them.
 *
 * Pure module: no React, no Three.js. `rotationRadiansY` is the single place
 * that converts to the viewer's `group.rotation.y`.
 */

import type { SaunaDimensions } from "../../engines/types";
import type { SiteConditions } from "../foundation/siteConditions";

/** The four committable orientations, in degrees. */
export type SaunaRotationDeg = 0 | 90 | 180 | 270;

export const SAUNA_ROTATION_STEPS: readonly SaunaRotationDeg[] = [
	0, 90, 180, 270,
] as const;

export const DEFAULT_SAUNA_ROTATION_DEG: SaunaRotationDeg = 0;

/** Degrees per snap step. */
export const SAUNA_ROTATION_STEP_DEG = 90;

/**
 * Half-width of the magnet zone around each cardinal angle during a live
 * gesture. Inside it the preview locks onto the cardinal so the user can see
 * where the release will land; outside it the raw angle is shown.
 */
export const SAUNA_ROTATION_MAGNET_DEG = 10;

/** Wrap any angle into `[0, 360)`. Tolerates negatives and multiple turns. */
export function normalizeAngleDeg(deg: number): number {
	if (!Number.isFinite(deg)) return 0;
	return ((deg % 360) + 360) % 360;
}

/** Signed shortest difference `a - b`, in `(-180, 180]`. */
export function shortestAngleDeltaDeg(a: number, b: number): number {
	const delta = normalizeAngleDeg(a - b);
	return delta > 180 ? delta - 360 : delta;
}

/**
 * Snap anything to the nearest committable orientation.
 *
 * Deliberately total: a corrupt persisted value, a `NaN` from a bad drag, or
 * `undefined` from a design saved before this field existed all resolve to a
 * usable angle rather than propagating into `group.rotation.y`.
 */
export function snapRotationDeg(deg: unknown): SaunaRotationDeg {
	if (typeof deg !== "number" || !Number.isFinite(deg)) {
		return DEFAULT_SAUNA_ROTATION_DEG;
	}
	const normalized = normalizeAngleDeg(deg);
	const step = Math.round(normalized / SAUNA_ROTATION_STEP_DEG) % 4;
	return SAUNA_ROTATION_STEPS[step];
}

/**
 * Preview angle for a live gesture: free rotation, with a hard magnet inside
 * `magnetDeg` of a cardinal so the snap that will happen on release is
 * visible before the user commits to it.
 */
export function magnetizeRotationDeg(
	deg: number,
	magnetDeg: number = SAUNA_ROTATION_MAGNET_DEG,
): number {
	const normalized = normalizeAngleDeg(deg);
	const nearest = snapRotationDeg(normalized);
	return Math.abs(shortestAngleDeltaDeg(normalized, nearest)) <= magnetDeg
		? nearest
		: normalized;
}

/**
 * Step the orientation by whole quarter turns. Positive `steps` turns the
 * building clockwise seen from above (the direction a "⟳" button implies),
 * which is the negative direction of `rotation.y`.
 */
export function stepRotationDeg(
	current: unknown,
	steps: number,
): SaunaRotationDeg {
	const base = snapRotationDeg(current);
	const whole = Number.isFinite(steps) ? Math.round(steps) : 0;
	return snapRotationDeg(base - whole * SAUNA_ROTATION_STEP_DEG);
}

/** Viewer-facing conversion: degrees → `group.rotation.y` in radians. */
export function rotationRadiansY(deg: number): number {
	return (normalizeAngleDeg(deg) * Math.PI) / 180;
}

/**
 * True when the orientation exchanges the world X and Z extents — i.e. the
 * building's width now runs along world Z. Every world-axis-aligned consumer
 * (grounding footprint, grass keep-out, section axis) keys off this.
 */
export function swapsFootprintAxes(deg: unknown): boolean {
	const snapped = snapRotationDeg(deg);
	return snapped === 90 || snapped === 270;
}

/**
 * The building's footprint as the *world* sees it. Rotating 90° or 270° hands
 * the world a `length × width` box where it used to see `width × length`.
 */
export function rotateFootprintDimensions<T extends SaunaDimensions>(
	dimensions: T,
	deg: unknown,
): T {
	if (!swapsFootprintAxes(deg)) return dimensions;
	return { ...dimensions, width: dimensions.length, length: dimensions.width };
}

/**
 * Model-local (x, z) → world (x, z) for a group carrying
 * `rotation.y = rotationRadiansY(deg)`.
 *
 * Three's Y rotation matrix is `[c 0 s; 0 1 0; -s 0 c]`, so
 * `x' = x·cos + z·sin`, `z' = -x·sin + z·cos`.
 */
export function toWorldXZ(
	x: number,
	z: number,
	deg: number,
): { x: number; z: number } {
	const rad = rotationRadiansY(deg);
	const cos = Math.cos(rad);
	const sin = Math.sin(rad);
	return { x: x * cos + z * sin, z: -x * sin + z * cos };
}

/**
 * Which world-facing side of the plot a model wall ends up on.
 *
 * Wall normals are front = +Z, back = -Z, left = -X, right = +X, so this is
 * {@link toWorldXZ} applied to the normal and read back as a side. Needed
 * wherever the deck's `attachWall` meets a world-axis question — e.g. "does
 * this deck stick out towards the lake?".
 */
export type FootprintWall = "front" | "back" | "left" | "right";

const WALL_BY_QUARTER_TURN: Record<FootprintWall, FootprintWall[]> = {
	// index = rotation / 90
	front: ["front", "right", "back", "left"],
	back: ["back", "left", "front", "right"],
	left: ["left", "front", "right", "back"],
	right: ["right", "back", "left", "front"],
};

export function rotateAttachWall(
	wall: FootprintWall,
	deg: unknown,
): FootprintWall {
	const quarter = snapRotationDeg(deg) / SAUNA_ROTATION_STEP_DEG;
	return WALL_BY_QUARTER_TURN[wall][quarter];
}

/**
 * Re-express a sloping site in the **building's own frame**.
 *
 * The terrain is drawn outside the rig and evaluated in world coordinates,
 * but `calculateFoundation` reads the slope at MODEL pier positions — so once
 * the building can turn, one of the two has to move into the other's frame.
 * Rotating the gradient is the cheap direction: the ground mesh, the lake and
 * the shadow receiver all stay exactly where they were, and the piers get the
 * ground height that is really under them.
 *
 * Because the commit set is cardinal this is an exact axis swap rather than a
 * trig evaluation — no float residue leaking into a BOM figure. It agrees with
 * `toLocalXZ(across, along, deg)` (see the test), which is the general form.
 *
 * On a flat site this returns the input object unchanged, so the common case
 * cannot dirty `BuildInput` and rotation stays a pure transform. On a slope it
 * genuinely does change the build — which piers stand tall depends on which
 * way the building faces — and that is the point.
 */
export function rotateSlopeToModelFrame(
	siteConditions: SiteConditions,
	deg: unknown,
): SiteConditions {
	if (siteConditions.terrainMode !== "slope") return siteConditions;
	const across = siteConditions.slopeAcrossPercent;
	const along = siteConditions.slopeAlongPercent;
	switch (snapRotationDeg(deg)) {
		case 90:
			return {
				...siteConditions,
				slopeAcrossPercent: -along,
				slopeAlongPercent: across,
			};
		case 180:
			return {
				...siteConditions,
				slopeAcrossPercent: -across,
				slopeAlongPercent: -along,
			};
		case 270:
			return {
				...siteConditions,
				slopeAcrossPercent: along,
				slopeAlongPercent: -across,
			};
		default:
			return siteConditions;
	}
}

/**
 * Inverse of {@link rotateSlopeToModelFrame}.
 *
 * `BuildInput.siteConditions` carries the slope in the BUILDING's frame, so
 * anything that draws the terrain itself — the ground mesh, the water, the
 * shadow receiver — has to turn it back before it means anything in world
 * coordinates. Spelled out as its own function rather than a `-deg` call at
 * the site, because "which frame is this slope in" is exactly the question
 * that goes wrong silently.
 */
export function rotateSlopeToWorldFrame(
	siteConditions: SiteConditions,
	deg: unknown,
): SiteConditions {
	return rotateSlopeToModelFrame(siteConditions, -snapRotationDeg(deg));
}

/** World (x, z) → model-local (x, z). Inverse of {@link toWorldXZ}. */
export function toLocalXZ(
	x: number,
	z: number,
	deg: number,
): { x: number; z: number } {
	return toWorldXZ(x, z, -normalizeAngleDeg(deg));
}
